/**
 * Lightbox for chat image attachments: click a thumbnail → the image opens in
 * a dialog (instead of a new browser tab), with download / open-in-tab
 * actions. Esc, the X or a backdrop click closes it.
 */
import { Download, ExternalLink, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { formatFileSize } from './time'
import { cn } from '@/lib/utils'
import { mono } from '@/components/assistant-ui/elements/surfaces'

export interface LightboxImage {
  url: string
  filename: string
  size?: number
}

export function ImageLightbox({
  image,
  open,
  onOpenChange,
}: {
  image: LightboxImage
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('chat')
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="inset-0 top-0 left-0 m-auto h-fit w-fit translate-x-0 translate-y-0 max-w-[min(96vw,1400px)] border-none bg-transparent p-0 shadow-none outline-none sm:max-w-[min(96vw,1400px)]"
      >
        <DialogTitle className="sr-only">{image.filename}</DialogTitle>
        <figure className="overflow-hidden rounded-2xl bg-neutral-950 shadow-2xl">
          <img
            src={image.url}
            alt={image.filename}
            className="block max-h-[82vh] w-auto max-w-full object-contain"
          />
          <figcaption className="flex items-center gap-3 border-t border-white/10 px-4 py-2.5">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-white/90">
              {image.filename}
            </span>
            {image.size != null && image.size > 0 && (
              <span className={cn(mono, 'shrink-0 text-white/40')}>
                {formatFileSize(image.size)}
              </span>
            )}
            <a
              href={image.url}
              download={image.filename}
              aria-label={t('lightbox.download')}
              title={t('lightbox.download')}
              className="grid size-8 shrink-0 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Download className="size-4" />
            </a>
            <a
              href={image.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('lightbox.openInNewTab')}
              title={t('lightbox.openInNewTab')}
              className="grid size-8 shrink-0 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <ExternalLink className="size-4" />
            </a>
            <DialogClose asChild>
              <button
                type="button"
                aria-label={t('lightbox.close')}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="size-4" />
              </button>
            </DialogClose>
          </figcaption>
        </figure>
      </DialogContent>
    </Dialog>
  )
}
